<script setup lang="ts">
import { useBatteryBadge } from "@/components/status/composable/battery-badge";
import { StatusSourceType } from "@/stores";
import { useFeaturesStore } from "@/stores/features";
import { Badge, Icon, Tooltip } from "@soft-db/ui";
import { computed } from "vue";
import { useLiveDataBadge } from "./composable/live-data-badge";
import { useLiveStatus } from "./composable/live-status";
import { useStateBadge } from "./composable/state-badge";

const props = defineProps<{
	vertical?: boolean;
	xs?: boolean;
	sourceType: StatusSourceType;
	sourceId: string;
}>();

const status = useLiveStatus(props.sourceType, () => props.sourceId);

const { hasFeature } = useFeaturesStore();

const displayBattery = computed(() => {
	return hasFeature("integrity") && !!status.value?.battery;
});

const stateBadge = useStateBadge(() => status.value?.state);
const batteryBadge = useBatteryBadge(status);
const liveDataBadge = useLiveDataBadge(status);
</script>

<template>
	<div
		v-if="status != null"
		:class="{
			'grid-auto': true,
			'grid gap-1 text-sm *:w-full': true,
			'auto-cols-fr grid-flow-col': !vertical,
		}"
	>
		<!-- State -->
		<Badge v-if="stateBadge" :color="stateBadge.color" class="select-none" variant="soft">
			<Icon size="xs" :d="stateBadge.icon" />
			<p :class="{ 'text-nowrap text-xs': xs }">{{ stateBadge.text }}</p>
		</Badge>

		<!-- Battery -->
		<Badge
			v-if="displayBattery && batteryBadge != null"
			class="select-none"
			:color="batteryBadge.color"
			variant="soft"
		>
			<Icon size="xs" :d="batteryBadge.icon" />
			<p :class="{ 'text-nowrap text-xs': xs }">{{ batteryBadge.value }}%</p>
		</Badge>

		<!-- Live data -->

		<Tooltip :tooltip="liveDataBadge?.name">
			<Badge v-if="liveDataBadge" class="select-none truncate px-0" variant="soft">
				<Icon size="xs" :d="liveDataBadge.icon" />
				<p :class="{ 'text-nowrap text-xs': xs }">{{ liveDataBadge.value }}</p>
			</Badge>
		</Tooltip>
	</div>
</template>
